import React, { useState } from 'react';

import {
  closestCenter,
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  restrictToParentElement,
  restrictToVerticalAxis,
} from '@dnd-kit/modifiers';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import PropTypes from 'prop-types';

import FieldOption from 'components/settings/personnel/information/FieldOption';

const minimumNumber = 2;

export default function FieldOptions({
  fieldError,
  onOptionDelete,
  onOptionOrderChange,
  onOptionValueChange,
  options,
  removeErrorStyling,
}) {
  const sensors = useSensors(useSensor(PointerSensor));

  const [isSorting, setIsSorting] = useState(false);

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = orderedIds.indexOf(active.id);
      const newIndex = orderedIds.indexOf(over.id);
      onOptionOrderChange({ oldIndex, newIndex });
    }

    setIsSorting(false);
  };

  const { allDataIds: orderedIds, data } = options;

  const numberOfOptions = orderedIds.length;
  const isGreaterThanMinimumNumberOfOptions = numberOfOptions > minimumNumber;

  const isFieldErrorHighlighted = !!fieldError?.fieldHighlighted;

  const optionValueCount =
    isFieldErrorHighlighted &&
    Object.values(data).reduce((accumulator, option) => {
      accumulator[option.value] =
        accumulator[option.value] ? accumulator[option.value] + 1 : 1;
      return accumulator;
    }, {});

  return (
    <DndContext
      collisionDetection={closestCenter}
      modifiers={[restrictToParentElement, restrictToVerticalAxis]}
      onDragEnd={handleDragEnd}
      onDragStart={() => setIsSorting(true)}
      sensors={sensors}
    >
      <SortableContext
        items={orderedIds}
        strategy={verticalListSortingStrategy}
      >
        {orderedIds.map((id, index) => {
          const option = data[id];

          const isInvalid =
            isFieldErrorHighlighted &&
            (!option.value.trim() || optionValueCount[option.value] > 1);

          return (
            <FieldOption
              isAutoFocused={
                isGreaterThanMinimumNumberOfOptions ?
                  index === numberOfOptions - 1
                : index === 0
              }
              isDeletable={isGreaterThanMinimumNumberOfOptions}
              isInvalid={isInvalid}
              isSorting={isSorting}
              key={id}
              onOptionDelete={onOptionDelete}
              onOptionValueChange={onOptionValueChange}
              option={option}
              removeErrorStyling={removeErrorStyling}
            />
          );
        })}
      </SortableContext>
    </DndContext>
  );
}

FieldOptions.propTypes = {
  fieldError: PropTypes.exact({
    error: PropTypes.object,
    fieldHighlighted: PropTypes.bool,
  }),
  onOptionValueChange: PropTypes.func.isRequired,
  onOptionDelete: PropTypes.func.isRequired,
  onOptionOrderChange: PropTypes.func.isRequired,
  options: PropTypes.exact({
    allDataIds: PropTypes.array.isRequired,
    data: PropTypes.object.isRequired,
  }).isRequired,
  removeErrorStyling: PropTypes.func.isRequired,
};
